/**
 * Formats a skill or subSkill string into a readable title.
 * Replaces underscores/hyphens with spaces and capitalizes each word.
 * Example: 'breuken_vergelijken' -> 'Breuken Vergelijken'
 * Example: 'basisrekenen' -> 'Basisrekenen'
 */
export function formatSkill(skill: string): string {
  if (!skill) return ''

  // Replace underscores and hyphens with spaces
  const withSpaces = skill.replace(/[_-]/g, ' ')

  // Capitalize first letter of each word
  return withSpaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Formats a timestamp into a relative Dutch distance string.
 * Example: "Vandaag", "Gisteren", "3 dagen geleden", "Vorige week"
 */
export function formatDistanceToNow(timestamp: number): string {
  const now = Date.now()
  const diffMs = now - timestamp
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) return 'Zojuist'
  if (diffMinutes < 60) return `${diffMinutes} min geleden`
  if (diffHours < 24) {
    // Check if it's still "today"
    const nowDate = new Date(now)
    const tsDate = new Date(timestamp)
    if (nowDate.getDate() === tsDate.getDate()) return 'Vandaag'
    return 'Gisteren'
  }
  if (diffDays === 1) return 'Gisteren'
  if (diffDays < 7) return `${diffDays} dagen geleden`
  if (diffDays < 14) return 'Vorige week'
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weken geleden`
  return `${Math.floor(diffDays / 30)} maanden geleden`
}

/**
 * Maps a mastery percentage to a human-readable Dutch context label.
 * Used in parent/teacher portals to give meaning to raw percentages.
 */
export function getMasteryLabel(mastery: number): {
  label: string
  colorClass: string
} {
  if (mastery >= 90)
    return { label: 'Uitstekend', colorClass: 'text-emerald-600' }
  if (mastery >= 75)
    return { label: 'Goed op weg', colorClass: 'text-emerald-500' }
  if (mastery >= 60) return { label: 'Op schema', colorClass: 'text-sky-600' }
  if (mastery >= 40)
    return { label: 'In ontwikkeling', colorClass: 'text-amber-600' }
  if (mastery >= 20)
    return { label: 'Nog veel te leren', colorClass: 'text-orange-600' }
  return { label: 'Net begonnen', colorClass: 'text-slate-500' }
}

// ── Date Formatting Helpers (D-SCAN-2 D2-6) ─────────────────────────

const NL_LOCALE = 'nl-NL'

/**
 * Format a timestamp as a short Dutch date: "22 apr"
 */
export function formatDateShort(ts: number | undefined | null): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString(NL_LOCALE, {
    day: 'numeric',
    month: 'short',
  })
}

/**
 * Format a timestamp as a long Dutch date: "22 april 2026"
 */
export function formatDateLong(ts: number | undefined | null): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString(NL_LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/**
 * Format a timestamp as a Dutch date with time: "22 apr 14:30"
 */
export function formatDateWithTime(ts: number | undefined | null): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleString(NL_LOCALE, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format a timestamp as a relative Dutch time string.
 * Consolidated from formatDistanceToNow() and admin timeAgo() (D-SCAN-2 D2-7).
 *
 * @param compact - If true, uses short format ("3m", "2u", "5d"). Default: false (long format).
 */
export function formatRelativeTime(
  ts: number | undefined | null,
  compact: boolean = false,
): string {
  if (!ts) return '—'
  const diffMs = Date.now() - ts
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) return compact ? 'zojuist' : 'Zojuist'
  if (diffMinutes < 60)
    return compact ? `${diffMinutes}m geleden` : `${diffMinutes} min geleden`
  if (diffHours < 24) {
    if (compact) return `${diffHours}u geleden`
    const nowDate = new Date()
    const tsDate = new Date(ts)
    if (nowDate.getDate() === tsDate.getDate()) return 'Vandaag'
    return 'Gisteren'
  }
  if (diffDays === 1) return 'Gisteren'
  if (diffDays < 7)
    return compact ? `${diffDays}d geleden` : `${diffDays} dagen geleden`
  if (diffDays < 14) return compact ? '1w geleden' : 'Vorige week'
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7)
    return compact ? `${weeks}w geleden` : `${weeks} weken geleden`
  }
  // Fall back to short date for old timestamps
  return formatDateShort(ts)
}
